import { Badge } from '../../ui/badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../ui/table';
import { format, parseISO } from 'date-fns';
import { formatNumber, formatCurrency } from '../../dashboard';
import { DetailedRow } from '../../../services/api/CPTAnalytics/CPTActivityService';

interface CPTDetailedTableProps {
    rows: DetailedRow[];
    loading: boolean;
    start?: string;
    end?: string;
}

export default function CPTDetailedTable({
    rows: rawRows,
    loading,
    start,
    end,
}: CPTDetailedTableProps) {
    const rows = Array.isArray(rawRows) ? rawRows : [];

    const totalQty = rows.reduce((sum, row) => sum + row.quantity, 0);
    const totalRVU = rows.reduce((sum, row) => sum + row.totalRVU, 0);
    const totalRevenue = rows.reduce((sum, row) => sum + row.estimatedRevenue, 0);

    return (
        <div>
            {rows.length > 0 ? (
                <>
                {start && end && (
                    <p className="mb-3 text-base font-medium text-foreground">
                        Reports with scan date {format(parseISO(start), 'MMMM d, yyyy')} to {format(parseISO(end), 'MMMM d, yyyy')}
                    </p>
                )}
                <div className="overflow-auto rounded-lg border border-subtle">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Scan Date</TableHead>
                                <TableHead>User</TableHead>
                                <TableHead>Device Type</TableHead>
                                <TableHead>CPT</TableHead>
                                <TableHead className="text-right">Qty</TableHead>
                                <TableHead className="text-right">RVU/Unit</TableHead>
                                <TableHead className="text-right">Total RVU</TableHead>
                                <TableHead className="text-right">Est. Revenue</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.map((row, i) => (
                                <TableRow
                                    key={`${row.date}-${row.techName}-${row.cptCode}-${i}`}
                                >
                                    <TableCell className="whitespace-nowrap tabular-nums">{row.date}</TableCell>
                                    <TableCell>{row.techName}</TableCell>
                                    <TableCell>
                                        {row.deviceType || '—'}
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant="outline" className="text-[#6AA2FF] border-[#6AA2FF]">
                                            {row.cptCode}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {row.quantity}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {formatNumber(row.rvuPerUnit)}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {formatNumber(row.totalRVU)}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {formatCurrency(row.estimatedRevenue)}
                                    </TableCell>
                                </TableRow>
                            ))}
                            <TableRow className="border-t-2 border-subtle font-medium">
                                <TableCell colSpan={4}>Total</TableCell>
                                <TableCell className="text-right tabular-nums">{totalQty}</TableCell>
                                <TableCell />
                                <TableCell className="text-right tabular-nums">{formatNumber(totalRVU)}</TableCell>
                                <TableCell className="text-right tabular-nums">{formatCurrency(totalRevenue)}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </div>
                </>
            ) : (
                !loading && (
                    <div className="rounded-lg border border-dashed border-subtle py-12 text-center">
                        <p className="text-sm text-muted-foreground">
                            Select a date range and click <span className="font-medium text-foreground">Run Analytics</span> to generate your report.
                        </p>
                    </div>
                )
            )}
        </div>
    );
}
